import { ProviderJobTimeoutError } from "@loumy/contracts";
import type {
  FrameInterpolationProvider,
  FrameInterpolationRequest,
  FrameInterpolationResult,
  GpuCompute,
  ImageBgRemoveProvider,
  ImageBgRemoveRequest,
  ImageBgRemoveResult,
  ImageEditProvider,
  ImageEditRequest,
  ImageEditResult,
  ImageGenRequest,
  ImageProvider,
  ImageUpscaleProvider,
  ImageUpscaleRequest,
  ImageUpscaleResult,
  ProviderImageResult,
  ProviderJobHandle,
  ProviderJobState,
  ProviderVideoOutput,
  VideoBgRemoveProvider,
  VideoBgRemoveRequest,
  VideoBgRemoveResult,
  VideoEnhanceProvider,
  VideoEnhanceRequest,
  VideoEnhanceResult,
  VideoGenRequest,
  VideoInpaintProvider,
  VideoInpaintRequest,
  VideoInpaintResult,
  VideoProvider,
} from "@loumy/contracts";

// ProviderJobTimeoutError lebt jetzt im Provider-Vertrag (contracts) — der Runner unterscheidet
// damit "Tick-Budget erschöpft → Resume" von echten Fehlern. Hier re-exportiert (Rückwärtskompat).
export { ProviderJobTimeoutError };

// §3.7/D27 — generischer Provider-Job-Treiber für provider-backed Capabilities (video-gen +
// image-gen). Reihenfolge: generate → Handle persistieren (VOR dem ersten Poll) → poll-Schleife.
// Erschöpft das PRO-TICK-Budget (DEFAULT_TICK_ATTEMPTS), ohne dass der Provider failed, wirft er
// `ProviderJobTimeoutError` — der Runner pausiert dann den Job (Resume) statt zu failen, und ein
// späterer Tick ruft denselben Treiber mit `resumeHandle` (generate ÜBERSPRUNGEN, nur weiterpollen).
// Video- und Image-Provider erfüllen beide das `AsyncProvider`-Interface (generate→Handle,
// poll→State) — ein gemeinsamer Kern, zwei typisierte Einstiege.

export class ProviderJobFailedError extends Error {
  constructor(
    readonly handle: ProviderJobHandle,
    readonly providerError: string,
  ) {
    super(`Provider-Job '${handle.externalId}' (${handle.providerId}@${handle.providerVersion}) gescheitert: ${providerError}`);
    this.name = "ProviderJobFailedError";
  }
}

export interface RunProviderJobOptions {
  /** Persistiert das Handle VOR dem Polling (D27) — Resume kann dann re-pollen. */
  persistHandle?: (handle: ProviderJobHandle) => Promise<void>;
  /** Resume: bestehendes Handle → generate() wird ÜBERSPRUNGEN, nur poll() (kein Re-generate). */
  resumeHandle?: ProviderJobHandle | null;
  signal?: AbortSignal;
  /** Poll-Limit PRO TICK (Default `DEFAULT_TICK_ATTEMPTS`); Erschöpfung → Resume (D27). */
  maxAttempts?: number;
  onProgress?: (pct: number) => void;
  /** injizierbar → deterministische Tests (Default: echtes setTimeout). */
  sleep?: (ms: number) => Promise<void>;
  /** Poll-Intervall in ms (Default 5000, D106 — je Poll 1 Subrequest, D104). */
  intervalMs?: number;
}

/** Rückwärtskompatibler Alias (videoGen nutzte diesen Namen). */
export type RunVideoJobOptions = RunProviderJobOptions;

// Pro-Tick-Poll-Budget (D27): klein genug, dass der Worker schnell pausiert statt bis zum harten
// Job-Exec-Timeout (consumer.ts, 660 s seit D167) zu blockieren — der Rest läuft über Resume-Ticks. ~90 s
// pro Tick; zusammen mit MAX_RESUME_TICKS (DB, 40) + 30 s Delay ⇒ Worst-Case ~80 min Geduld.
// D106: Intervall 1 s → 5 s (jeder Poll = 1 Subrequest; die Sekunden-Polls addierten sich über
// die Knoten ans Invocation-Limit, D104-Befund) — attempts angepasst, das Tick-FENSTER bleibt
// ~90 s. Ein SSE-Stream wie bei qwen-edit/FILM (D106) ist hier BEWUSST nicht im Spiel: poll()
// muss schnell zurückkehren, damit das Tick-Budget den Worker pausieren kann (D27/D67-Resume) —
// ein blockierender Stream hebelte genau das aus (§13-Option, falls je nötig).
const DEFAULT_TICK_ATTEMPTS = 18; // ≈ 90 s pro Tick bei intervalMs=5000
const defaultSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Async-Provider mit Handle/poll-Vertrag (D27) — Video und Image erfüllen ihn strukturell. */
interface AsyncProvider<TReq, TOut> {
  generate(req: TReq, opts?: { signal?: AbortSignal }): Promise<ProviderJobHandle>;
  poll(handle: ProviderJobHandle, opts?: { signal?: AbortSignal }): Promise<ProviderJobState<TOut>>;
}

/**
 * Treibt einen async Provider-Job bis `done` ODER bis das Tick-Budget erschöpft ist (D27).
 * Bei `resumeHandle` entfällt generate/persist (Resume). `failed` propagiert terminal
 * (ProviderJobFailedError); Tick-Erschöpfung mit Provider noch pending/processing → ProviderJobTimeoutError.
 */
async function runProviderJob<TReq, TOut>(
  provider: AsyncProvider<TReq, TOut>,
  req: TReq,
  opts: RunProviderJobOptions = {},
): Promise<TOut> {
  const { persistHandle, resumeHandle, signal, onProgress } = opts;
  const maxAttempts = opts.maxAttempts ?? DEFAULT_TICK_ATTEMPTS;
  const sleep = opts.sleep ?? defaultSleep;
  const intervalMs = opts.intervalMs ?? 5000; // D106: 5 s statt 1 s (Subrequest-Budget, D104)

  if (signal?.aborted) throw new Error("abgebrochen vor generate()");

  // Handle sichern, BEVOR persistiert/gepollt wird (D27): kein verlorener Provider-Job.
  // `signal` an generate/poll durchreichen → die HTTP-fetches brechen bei Job-Timeout/Cancel ab.
  const handle = resumeHandle ?? (await provider.generate(req, { signal }));
  if (!resumeHandle && persistHandle) await persistHandle(handle);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) throw new Error("abgebrochen während Polling");
    const state = await provider.poll(handle, { signal });
    switch (state.status) {
      case "done":
        return state.output;
      case "failed":
        throw new ProviderJobFailedError(handle, state.error);
      case "processing":
        if (typeof state.progress === "number") onProgress?.(state.progress);
        await sleep(intervalMs);
        break;
      case "pending":
        await sleep(intervalMs);
        break;
    }
  }
  throw new ProviderJobTimeoutError(handle);
}

/** Treibt einen VideoProvider-Job (D27, generate→persist→poll; Tick-Budget → Resume). */
export function runVideoProviderJob(
  provider: VideoProvider,
  req: VideoGenRequest,
  opts: RunProviderJobOptions = {},
): Promise<ProviderVideoOutput> {
  return runProviderJob<VideoGenRequest, ProviderVideoOutput>(provider, req, opts);
}

/** Treibt einen ImageProvider-Job (D27, generate→persist→poll; Tick-Budget → Resume). */
export function runImageProviderJob(
  provider: ImageProvider,
  req: ImageGenRequest,
  opts: RunProviderJobOptions = {},
): Promise<ProviderImageResult> {
  return runProviderJob<ImageGenRequest, ProviderImageResult>(provider, req, opts);
}

/**
 * Treibt einen VideoInpaintProvider-Job (D27, generate→persist→poll; Tick-Budget → Resume).
 * Umstellung vom synchronen `inpaint`-Wait (fal-vace.ts): die VACE-Naht wartet so nicht mehr in
 * EINEM Tick auf die volle fal-Queue (→ Job-Exec-Timeout, 660 s seit D167/Deploy-Eviction), sondern pausiert
 * nach dem Tick-Budget und pollt das persistierte Handle in einem späteren Resume-Tick weiter
 * (kein Re-Submit ⇒ keine doppelten fal-COGS). Output sind BYTES (videoBytes) — der Download
 * passiert im poll()-`done`-Zweig (anders als bei Video/Image-Gen, die eine URL liefern).
 */
export function runVideoInpaintProviderJob(
  provider: VideoInpaintProvider,
  req: VideoInpaintRequest,
  opts: RunProviderJobOptions = {},
): Promise<VideoInpaintResult> {
  return runProviderJob<VideoInpaintRequest, VideoInpaintResult>(provider, req, opts);
}

/**
 * Treibt einen ImageEditProvider-Job (D27, generate→persist→poll; Tick-Budget → Resume).
 * Umstellung vom synchronen `edit`-Wait (image-edit-provider.ts): die D76-Edit-Schleife in
 * `input-normalize` stirbt so nicht mehr an der vollen fal-Queue (war der `fal qwen-edit: nicht
 * fertig nach … Polls`-Job-Fail), sondern pausiert und pollt das persistierte Handle weiter.
 * Pro Edit-Runde ein eigenes Handle-Ordinal (persistHandle/resumeHandle vom Aufrufer gesetzt).
 */
export function runImageEditProviderJob(
  provider: ImageEditProvider,
  req: ImageEditRequest,
  opts: RunProviderJobOptions = {},
): Promise<ImageEditResult> {
  return runProviderJob<ImageEditRequest, ImageEditResult>(provider, req, opts);
}

/**
 * Treibt einen VideoBgRemoveProvider-Job (D146/D27, generate→persist→poll; Tick-Budget → Resume).
 * BEN2-Matting an voller fal-Queue stirbt so nicht in EINEM Tick, sondern pollt das Handle im
 * Resume-Tick weiter. Output sind BYTES (transparentes WebM) — Download im poll()-`done`-Zweig.
 */
export function runVideoBgRemoveProviderJob(
  provider: VideoBgRemoveProvider,
  req: VideoBgRemoveRequest,
  opts: RunProviderJobOptions = {},
): Promise<VideoBgRemoveResult> {
  return runProviderJob<VideoBgRemoveRequest, VideoBgRemoveResult>(provider, req, opts);
}

/**
 * Treibt einen VideoEnhanceProvider-Job (D168/D27, generate→persist→poll; Tick-Budget → Resume).
 * SeedVR2; Output ist eine URL (verbessertes MP4) — die Capability persistiert es wie ein gen-Video.
 */
export function runVideoEnhanceProviderJob(
  provider: VideoEnhanceProvider,
  req: VideoEnhanceRequest,
  opts: RunProviderJobOptions = {},
): Promise<VideoEnhanceResult> {
  return runProviderJob<VideoEnhanceRequest, VideoEnhanceResult>(provider, req, opts);
}

/**
 * D167: treibt einen FrameInterpolationProvider-Job über die OPTIONALE D27-Handle-Oberfläche
 * (generate/poll, fal-rife.ts). Für die frame-smooth-Schleife (bis 24 sequenzielle RIFE-Paare):
 * per-Paar-Handle (Ordinal = Paar-Index) macht die Schleife tick-sicher — Pause/Resume pollt
 * dasselbe Handle weiter (kein Re-Submit/Re-COGS). Der Aufrufer prüft die Präsenz von
 * generate/poll und fällt sonst auf den synchronen `interpolate`-Einzelaufruf zurück (FILM/D32).
 */
export function runFrameInterpolationProviderJob(
  provider: Required<Pick<FrameInterpolationProvider, "generate" | "poll">>,
  req: FrameInterpolationRequest,
  opts: RunProviderJobOptions = {},
): Promise<FrameInterpolationResult> {
  return runProviderJob<FrameInterpolationRequest, FrameInterpolationResult>(
    { generate: (r, o) => provider.generate(r, o), poll: (h, o) => provider.poll(h, o) },
    req,
    opts,
  );
}

/**
 * Treibt einen ImageBgRemoveProvider-Job (D146/D27, generate→persist→poll; Tick-Budget → Resume).
 * Bria-Matting; Output ist eine URL (transparentes PNG) — die Capability persistiert es wie ein gen-Bild.
 */
export function runImageBgRemoveProviderJob(
  provider: ImageBgRemoveProvider,
  req: ImageBgRemoveRequest,
  opts: RunProviderJobOptions = {},
): Promise<ImageBgRemoveResult> {
  return runProviderJob<ImageBgRemoveRequest, ImageBgRemoveResult>(provider, req, opts);
}

/**
 * Treibt einen ImageUpscaleProvider-Job (D163/D27, generate→persist→poll; Tick-Budget → Resume).
 * Real-ESRGAN; Output ist eine URL (hochskaliertes Bild) — die Capability persistiert es wie ein gen-Bild.
 */
export function runImageUpscaleProviderJob(
  provider: ImageUpscaleProvider,
  req: ImageUpscaleRequest,
  opts: RunProviderJobOptions = {},
): Promise<ImageUpscaleResult> {
  return runProviderJob<ImageUpscaleRequest, ImageUpscaleResult>(provider, req, opts);
}

/**
 * D159: treibt einen ASYNC GPU-Op (Modal-`spawn`/`pollSpawn`) durch DENSELBEN D27-Resume-Kern
 * wie die fal-Provider — der `GpuCompute`-Adapter SELBST IST hier der async Provider (generate =
 * `gpu.spawn(op,…)`→Handle mit der Modal `call_id`, poll = `gpu.pollSpawn(handle)`). Für GPU-Ops,
 * deren SYNCHRONE Antwort das Cloudflare-~100s-Origin-Timeout (524) reißen würde (loop-seam-vace/
 * VACE-Naht, ~105 s Inferenz): die Inferenz wird EINMAL gespawnt, das Handle persistiert (D27),
 * und bei Tick-Erschöpfung pausiert der Job + pollt im Resume-Tick dasselbe `call_id` weiter —
 * KEIN 524-Re-Run, KEIN re-COGS, scale-to-zero. Kein Registry-Provider / kein Pin ⇒ der Preis-Key
 * bleibt der Basis-Key (migrationsfrei). Der `op` reist im Handle-`raw` mit (resume-stabil).
 */
export function runGpuSpawnJob<O>(
  gpu: GpuCompute,
  op: string,
  input: Record<string, unknown>,
  opts: RunProviderJobOptions = {},
): Promise<O> {
  const provider: AsyncProvider<Record<string, unknown>, O> = {
    generate: (req, o) => gpu.spawn(op, req, o),
    poll: (handle, o) => gpu.pollSpawn<O>(handle, o),
  };
  return runProviderJob<Record<string, unknown>, O>(provider, input, opts);
}
